import React from "react";
import { X, Check, Lock, Crown } from "lucide-react";

const PLANS = [
  { key: "lite", label: "Lite", price: 29 },
  { key: "starter", label: "Starter", price: 49 },
  { key: "growth", label: "Growth", price: 99, popular: true },
  { key: "professional", label: "Professional", price: 199 },
  { key: "agency", label: "Agency", price: 399 },
];

const FEATURES = {
  seo_optimizer: "SEO Optimizer",
  site_audit: "Site Audit",
  web_vitals: "Web Vitals",
  ai_insights: "AI Insights",
  geo_monitor: "GEO Monitor",
  autopilot: "Auto-Pilot",
  backlinks: "Backlinks",
  content_pipeline: "Content Pipeline",
  alt_text_ai: "Alt Text AI",
  image_library: "Image Library",
  pdf_reports: "PDF Reports",
  email_alerts: "Email Alerts",
  calendar: "Calendar",
  internal_links: "Internal Links",
  syndication: "Syndication",
  white_label: "White Label",
  api_access: "API Access",
};

const PLAN_FEATURES = {
  lite: ["seo_optimizer", "site_audit", "web_vitals", "pdf_reports"],
  starter: ["seo_optimizer", "site_audit", "web_vitals", "backlinks", "alt_text_ai", "pdf_reports", "email_alerts"],
  growth: ["seo_optimizer", "site_audit", "web_vitals", "ai_insights", "geo_monitor", "autopilot", "backlinks", "content_pipeline", "alt_text_ai", "image_library", "pdf_reports", "email_alerts", "calendar", "internal_links", "syndication"],
  professional: ["seo_optimizer", "site_audit", "web_vitals", "ai_insights", "geo_monitor", "autopilot", "backlinks", "content_pipeline", "alt_text_ai", "image_library", "pdf_reports", "email_alerts", "calendar", "internal_links", "syndication", "api_access"],
  agency: Object.keys(FEATURES),
};

export default function UpgradeModal({ onClose, currentPlan = "trial", requiredFeature = null }) {
  const requiredPlanIdx = requiredFeature
    ? PLANS.findIndex((p) => (PLAN_FEATURES[p.key] || []).includes(requiredFeature))
    : -1;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto py-6" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-4xl p-6 space-y-5 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Crown size={18} className="text-copper" /> Upgrade Your Plan
            </h3>
            {requiredFeature && (
              <p className="text-sm text-gray-400 mt-1">
                <span className="text-copper">{FEATURES[requiredFeature] || requiredFeature}</span> requires upgrading your plan.
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {PLANS.map((plan, idx) => {
            const isCurrent = plan.key === currentPlan;
            const isRecommended = idx === requiredPlanIdx;
            const features = PLAN_FEATURES[plan.key] || [];

            return (
              <div key={plan.key}
                className={`relative bg-dark border rounded-xl p-4 space-y-3 ${
                  isRecommended ? "border-copper ring-1 ring-copper/30" : "border-dark-50"
                }`}>
                {isRecommended && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-copper text-white text-[10px] rounded-full font-medium">
                    Recommended
                  </div>
                )}
                <div>
                  <div className="text-sm font-medium">{plan.label}</div>
                  <div className="text-xl font-mono font-bold text-copper">${plan.price}<span className="text-xs text-gray-500">/mo</span></div>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(FEATURES).slice(0, 8).map(([fkey, flabel]) => (
                    <div key={fkey} className="flex items-center gap-1.5 text-[11px]">
                      {features.includes(fkey) ? (
                        <Check size={10} className="text-green-400 flex-shrink-0" />
                      ) : (
                        <X size={10} className="text-gray-700 flex-shrink-0" />
                      )}
                      <span className={features.includes(fkey) ? "text-gray-300" : "text-gray-600"}>{flabel}</span>
                    </div>
                  ))}
                </div>
                {isCurrent ? (
                  <div className="text-center text-xs text-gray-500 py-1.5">Current Plan</div>
                ) : (
                  <a href="mailto:gary@gcmm.nyc?subject=OnesuiteSEO%20Upgrade%20Request"
                    className={`block text-center text-xs py-1.5 rounded-lg font-medium ${
                      isRecommended
                        ? "bg-copper text-white hover:bg-copper-600"
                        : "bg-dark-50 text-gray-300 hover:text-white"
                    }`}>
                    Contact to Upgrade
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center text-xs text-gray-500">
          Need a custom plan? <a href="mailto:gary@gcmm.nyc" className="text-copper hover:underline">Contact us</a>
        </div>
      </div>
    </div>
  );
}
